import React, { useState, useEffect } from "react";
import ComponentCard from "../../../components/common/ComponentCard";
import StatCard from "../../../components/common/StatCard";
import BarChartOne from "../../../components/charts/bar/BarChartOne";
import LineChartOne from "../../../components/charts/line/LineChartOne";
import useApi, { GastosRentabilidadStats } from "../../../hooks/useApi";
import { DollarSign, TrendingDown, Percent, RefreshCw, Award, Clock } from "react-feather";
import { format, subMonths } from "date-fns";
import { toast } from "react-hot-toast";

// Helper function to convert color names to hex values
const getColorClass = (color: string): string => {
  const colorMap: Record<string, string> = {
    blue: "#3b82f6",   // bg-blue-500
    green: "#10b981",  // bg-green-500
    orange: "#f97316", // bg-orange-500
    red: "#ef4444",    // bg-red-500
    purple: "#8b5cf6", // bg-purple-500
    yellow: "#eab308", // bg-yellow-500
    indigo: "#6366f1", // bg-indigo-500
    gray: "#6b7280",   // bg-gray-500
  };
  
  return colorMap[color] || "#6b7280"; // Default to gray if color not found
};

export default function GastosRentabilidadView() {
  const [stats, setStats] = useState<GastosRentabilidadStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: format(subMonths(new Date(), 12), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const { formatCurrency, getGastosRentabilidadStats } = useApi();

  // Fetch data from the API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      
      try {
        // Get data from the API
        const data = await getGastosRentabilidadStats(
          dateRange.startDate,
          dateRange.endDate
        );
        
        // Round all numeric values to nearest integer
        data.gastosTotales = Math.round(data.gastosTotales);
        data.costoPromedioExcursion = Math.round(data.costoPromedioExcursion);
        data.rentabilidadPromedio = Math.round(data.rentabilidadPromedio);
        data.flujoEfectivoNeto = Math.round(data.flujoEfectivoNeto);
        data.saldoPendienteCobro = Math.round(data.saldoPendienteCobro);
        
        data.tendenciaGastos = Math.round(data.tendenciaGastos);
        data.tendenciaCostoPromedio = Math.round(data.tendenciaCostoPromedio);
        data.tendenciaRentabilidad = Math.round(data.tendenciaRentabilidad);
        data.tendenciaFlujoEfectivo = Math.round(data.tendenciaFlujoEfectivo);
        data.tendenciaSaldoPendiente = Math.round(data.tendenciaSaldoPendiente);
        
        if (data.excursionMasRentable && data.excursionMasRentable.rentabilidad) {
          data.excursionMasRentable.rentabilidad = Math.round(data.excursionMasRentable.rentabilidad);
        }
        
        data.distribucionGastos = data.distribucionGastos.map(item => ({
          ...item,
          monto: Math.round(item.monto)
        }));
        
        data.gastosPorMes = data.gastosPorMes.map(v => Math.round(v));
        data.rentabilidadPorMes = data.rentabilidadPorMes.map(v => Math.round(v));
        
        setStats(data);
      } catch (error) {
        console.error("Error fetching stats:", error);
        toast.error("Error al cargar estadísticas");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange, getGastosRentabilidadStats]);

  // Show skeleton UI when loading or when there's an error (stats is null)
  if (isLoading || !stats) {
    return (
      <div className="space-y-8">
        {/* Skeleton for key metrics */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <div className="animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="h-4 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div className="h-3 w-10 rounded bg-gray-200 dark:bg-gray-700"></div>
                </div>
                <div className="mt-3 h-6 w-2/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="mt-2 h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Skeleton for charts and detailed stats */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 lg:col-span-1">
            <div className="animate-pulse">
              <div className="h-5 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="mt-4 space-y-3">
                <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 lg:col-span-2">
            <div className="animate-pulse">
              <div className="h-5 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="h-24 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton for graphs */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <div className="animate-pulse">
                <div className="h-5 w-1/3 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="mt-2 h-3 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="mt-4 h-64 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const barChartData = [
    {
      name: "Gastos mensuales",
      data: stats.gastosPorMes,
    },
  ];

  const lineChartData = [
    {
      name: "Rentabilidad mensual",
      data: stats.rentabilidadPorMes,
    },
  ];

  // Function to handle date range changes (for future use)
  const handleDateRangeChange = (start: string, end: string) => {
    setDateRange({
      startDate: start,
      endDate: end
    });
  };

  return (
    <div className="space-y-8">
      {/* Métricas clave */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<DollarSign className="h-5 w-5" />}
            title="Gastos totales"
            value={formatCurrency(stats.gastosTotales)}
            trend={stats.tendenciaGastos}
            loading={false}
            description="Suma de costos operativos"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<TrendingDown className="h-5 w-5" />}
            title="Costo promedio por excursión"
            value={formatCurrency(stats.costoPromedioExcursion)}
            trend={stats.tendenciaCostoPromedio}
            loading={false}
            description="Gastos totales ÷ Número de excursiones"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<Percent className="h-5 w-5" />}
            title="Rentabilidad por excursión"
            value={`${stats.rentabilidadPromedio}%`}
            trend={stats.tendenciaRentabilidad}
            loading={false}
            description="(Ingresos - Costos) ÷ Ingresos × 100%"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<RefreshCw className="h-5 w-5" />}
            title="Flujo de efectivo neto"
            value={formatCurrency(stats.flujoEfectivoNeto)}
            trend={stats.tendenciaFlujoEfectivo}
            loading={false}
            description="Ingresos totales - Gastos totales"
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<Award className="h-5 w-5" />}
            title="Excursión más rentable"
            value={stats.excursionMasRentable.nombre || "Sin datos"}
            trend={0}
            loading={false}
            description={stats.excursionMasRentable.nombre ? `${stats.excursionMasRentable.rentabilidad}% de rentabilidad` : "No hay datos disponibles"}
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <StatCard
            icon={<Clock className="h-5 w-5" />}
            title="Saldo pendiente de cobro"
            value={formatCurrency(stats.saldoPendienteCobro)}
            trend={stats.tendenciaSaldoPendiente}
            loading={false}
            description="Reservas registradas, aún no pagadas"
          />
        </div>
      </div>

      {/* Gráficos y estadísticas detalladas */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Distribución de gastos */}
        <ComponentCard
          title="Distribución de gastos"
          className="lg:col-span-1"
        >
          {stats.distribucionGastos.length > 0 ? (
            <div className="space-y-4">
              {stats.distribucionGastos.map((item: {categoria: string; monto: number; color: string}, index: number) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="h-3 w-3 rounded-full" 
                      style={{ backgroundColor: getColorClass(item.color) }}
                    ></div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {item.categoria}
                    </span>
                  </div>
                  <span className="text-lg font-medium text-gray-900 dark:text-white">
                    {formatCurrency(item.monto)}
                  </span>
                </div>
              ))}

              <div className="mt-4 flex h-4 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                {stats.distribucionGastos.map((item: {categoria: string; monto: number; color: string}, index: number) => (
                  <div 
                    key={index}
                    className="h-full"
                    style={{ 
                      width: `${Math.round((item.monto / stats.gastosTotales) * 100)}%`,
                      backgroundColor: getColorClass(item.color)
                    }}
                  ></div>
                ))}
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                {stats.distribucionGastos[0].categoria}: {Math.round((stats.distribucionGastos[0].monto / stats.gastosTotales) * 100)}% del total de gastos
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="mb-3 h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-base text-gray-600 dark:text-gray-400">No hay datos de gastos disponibles</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">No se han registrado gastos en el período seleccionado</p>
            </div>
          )}
        </ComponentCard>

        {/* Lo más destacado */}
        <ComponentCard
          title="Lo más destacado"
          className="lg:col-span-2"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <TrendingDown className="h-5 w-5 text-red-500" />
                </div>
                <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">
                  Mes con más gastos
                </h4>
              </div>
              <div className="pl-11">
                {stats.gastosPorMes.some(value => value > 0) && stats.mesMasGastos ? (
                  <>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {stats.mesMasGastos}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {formatCurrency(Math.max(...stats.gastosPorMes))} en gastos totales
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      Sin datos
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      No hay registros de gastos en este período
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <Percent className="h-5 w-5 text-green-500" />
                </div>
                <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">
                  Mes más rentable
                </h4>
              </div>
              <div className="pl-11">
                {stats.rentabilidadPorMes.some(value => value > 0) && stats.mesMasRentable ? (
                  <>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {stats.mesMasRentable}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {Math.max(...stats.rentabilidadPorMes)}% de rentabilidad
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      Sin datos
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      No hay cálculos de rentabilidad en este período
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </ComponentCard>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ComponentCard
          title="Evolución de gastos mensuales"
          desc="Gastos totales por mes"
        >
          {stats.gastosPorMes.some(value => value > 0) ? (
            <div className="h-80">
              <BarChartOne series={barChartData} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-80 text-center">
              <div className="mb-3 h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-base text-gray-600 dark:text-gray-400">No hay datos de gastos mensuales</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">No se han registrado gastos en el período seleccionado</p>
            </div>
          )}
        </ComponentCard>

        <ComponentCard
          title="Tendencia de rentabilidad"
          desc="Porcentaje de rentabilidad mensual"
        >
          {stats.rentabilidadPorMes.some(value => value > 0) ? (
            <div className="h-80">
              <LineChartOne series={lineChartData} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-80 text-center">
              <div className="mb-3 h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <Percent className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-base text-gray-600 dark:text-gray-400">No hay datos de rentabilidad mensual</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">No se ha calculado la rentabilidad en el período seleccionado</p>
            </div>
          )}
        </ComponentCard>
      </div>
    </div>
  );
} 